import { NgZone } from '@angular/core';
import { PushHandler } from './push-handler';
import { BehaviorSubject, Observable, of, ReplaySubject } from 'rxjs';
import { catchError, delay, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { PushData } from './push-data';
import { ILogger } from '../logger/logger.service';
import { ActionPerformed, PushNotifications, PushNotificationSchema, Token } from '@capacitor/push-notifications';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';
import { AppStateService } from '../app-state/app-state.service';

export class CapacitorPushHandlerService implements PushHandler {
  supported$ = new BehaviorSubject<boolean>(false);
  permission$ = new BehaviorSubject<NotificationPermission>('default');
  permissionGranted$ = this.permission$.pipe(map((permission) => permission === 'granted'));
  private tokenChanges$ = new BehaviorSubject<string | null>(null);
  foregroundMessage$ = new ReplaySubject<PushData>(1);

  private firebaseProjectId: string;

  constructor(
    private logger: ILogger,
    private ngZone: NgZone,
    private router: Router,
    private appStateService: AppStateService,
  ) {}

  getFirebaseProjectId(): string {
    return this.firebaseProjectId;
  }

  init(config: any): Observable<void> {
    this.firebaseProjectId = config.projectId;

    PushNotifications.addListener('registration', (token: Token) => {
      this.logger.log('registration', token.value);
      this.tokenChanges$.next(token.value);
    });

    PushNotifications.addListener('registrationError', (error) => {
      this.logger.error('registrationError', error);
    });

    this.ngZone.runOutsideAngular(() => {
      PushNotifications.addListener('pushNotificationActionPerformed', (actionPerformed: ActionPerformed) => {
        this.logger.log('pushNotificationActionPerformed', actionPerformed);
        if (actionPerformed.notification.data.appRoute) {
          this.ngZone.run(() => this.openNotification(actionPerformed.notification));
        }
        PushNotifications.removeAllDeliveredNotifications();
      });

      PushNotifications.addListener('pushNotificationReceived', (notification) => {
        this.logger.log('pushNotificationReceived', notification);
        if (notification.data.appRoute) {
          this.ngZone.run(() => {
            if (this.appStateService.isVisible.value) {
              this.foregroundMessage$.next({
                ...notification.data,
                title: notification.title,
                body: notification.body,
              });
            } else {
              this.openNotification(notification);
            }
          });
        }
        PushNotifications.removeAllDeliveredNotifications();
      });
    });

    //this.checkPermission().subscribe(this.permission$);

    this.supported$.next(true);

    this.appStateService.addOnTerminateCallback(() => PushNotifications.removeAllListeners());

    return of(null);
  }

  private openNotification(notification: PushNotificationSchema) {
    if (notification.data.appRoute) {
      if (notification.data.institutionId) {
        this.router.navigateByUrl(
          `/institution/${notification.data.institutionId}?target=${encodeURIComponent(notification.data.appRoute)}`,
        );
      } else {
        this.router.navigateByUrl(notification.data.appRoute);
      }
      this.clearApplicationIconBadge().pipe(take(1)).subscribe();
    }
  }

  private checkPermission(): Observable<NotificationPermission> {
    return fromPromise(PushNotifications.checkPermissions()).pipe(
      map((permissionStatus) => {
        switch (permissionStatus.receive) {
          case 'granted':
            return 'granted';
          case 'denied':
            return 'denied';
          case 'prompt':
          case 'prompt-with-rationale':
          default:
            return 'default';
        }
      }),
    );
  }

  requestPermission(): Observable<NotificationPermission> {
    return this.checkPermission().pipe(
      switchMap((permission): Observable<NotificationPermission> => {
        if (permission === 'default') {
          return fromPromise(PushNotifications.requestPermissions()).pipe(
            map((permissionStatus) => {
              if (permissionStatus.receive === 'granted') {
                return 'granted';
              } else if (permissionStatus.receive === 'prompt' || permissionStatus.receive === 'prompt-with-rationale') {
                return 'default';
              } else {
                return 'denied';
              }
            }),
          );
        } else {
          return of(permission);
        }
      }),
      tap((permission) => this.permission$.next(permission)),
    );
  }

  register(): Observable<string> {
    const tokenBeforeRegistration = this.tokenChanges$.value;
    return fromPromise(PushNotifications.register()).pipe(
      switchMap(() => {
        let tokenChangesCounter = 0;
        return this.tokenChanges$.pipe(
          tap(() => tokenChangesCounter++),
          filter((newToken) => {
            this.logger.log('token changes', tokenBeforeRegistration, newToken);
            if (newToken && newToken !== tokenBeforeRegistration) {
              return true;
            }
          }),
        );
      }),
      take(1), // complete after one "real" token change
    );
  }

  unregister(): Observable<void> {
    return fromPromise(PushNotifications.unregister()).pipe(tap(() => this.tokenChanges$.next(null)));
  }

  clearApplicationIconBadge(): Observable<void> {
    return fromPromise(PushNotifications.checkPermissions()).pipe(
      switchMap((permissionStatus) => {
        if (permissionStatus.receive !== 'granted') {
          // No permission means no notifications to clear
          return of(null);
        }

        return fromPromise(
          PushNotifications.removeAllDeliveredNotifications().catch((error) => {
            // Silently ignore errors (e.g., iOS "CapacitorDidRegisterForRemoteNotifications not called")
            this.logger.warn('clearApplicationIconBadge failed (may be expected on fresh install)', error);
            return null;
          }),
        );
      }),
    );
  }
}
